import { useCallback, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useStore } from 'zustand';

import { useGoogleMaps } from './use-google-maps.hook';
import { useRecentLocationSearches } from './use-recent-location-searches.hook';

import { AutocompletePrediction, Place } from '@/types/google-maps.types';
import { CustomerAddress } from '@/types/auth-base.types';
import { addressFormatter } from '@/utils/formatters/format-address';
import { getCitySlug } from '@/app/(main)/(pages)/_services/get-city-slug.service';
import { useSearchParams } from '@/hooks/use-search-params.hook';
import { PersistedUserNavigationSearchParams } from '@/types/enums/persisted-user-navigation-search-params.enum';
import { slugify } from '@/utils/formatters/slugfy';
import { setUserLocation } from '@/store/user-location.store';
import { setOpenLocationSelectionModal, setDeniedGeolocation, modalsOpeningStore } from '@/store/modal';
import { openSearchModal } from '@/store/search/search.store';
import { branchesListId } from '@/app/(main)/(pages)/_components/branches-section/consts/branches-section.const';
import { getLogger } from '@/logger/logger';
import { getAnalyticsPlugin } from '@/app/(main)/_components/logger/analytics/get-analytics-plugin';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';
import { buildUrlWithPersistedParams } from '@/utils/persisted-params.util';
import { useLoading } from '@/hooks/use-loading.hook';

const MODAL_CLOSE_DELAY = 100;
const GEOLOCATION_PERMISSION_DENIED = 1;

const logger = getLogger('location.selection');

function isGeolocationPermissionDenied(error: unknown): boolean {
  return (error as GeolocationPositionError | undefined)?.code === GEOLOCATION_PERMISSION_DENIED;
}

function buildPredictionFromPlace(place: Place): AutocompletePrediction {
  return {
    placeId: place.placeId,
    description: place.formattedAddress
      || `${place.city}${place.state ? `, ${place.state}` : ''}`,
    formattedAddress: place.formattedAddress,
    latitude: place.latitude,
    longitude: place.longitude,
    types: place.types,
    city: place.city,
    state: place.state,
    street: place.street,
    locationType: place.locationType,
  };
}

export function useLocationSelection() {
  const router = useRouter();
  const searchParams = useSearchParams([...Object.values(PersistedUserNavigationSearchParams)]);
  const analytics = getAnalyticsPlugin();

  const [hasSelectionError, setHasSelectionError] = useState(false);
  const [hasGeolocationError, setHasGeolocationError] = useState(false);

  const {
    isLoading: isGeolocating,
    startLoading: startGeolocating,
    stopLoading: stopGeolocating,
  } = useLoading();

  const geolocationRequestRef = useRef<symbol | null>(null);

  const {
    getGoogleMapsPredictions,
    getGoogleMapsPlaceDetailsWithGeocoder,
    getGoogleMapsPlaceByCoordinates,
    getGoogleMapsPlaceByAddress,
    getGoogleMapsPlaceByCityAndState,
    resetSessionToken,
  } = useGoogleMaps();

  const { addRecentSearch } = useRecentLocationSearches();

  const {
    locationCallback,
    pendingSearchTerm,
    pendingSearchContext,
    shouldOpenSearchModal,
    shouldScrollToBranchesListOnNavigate,
  } = useStore(modalsOpeningStore, (state) => ({
    locationCallback: state?.locationCallback,
    pendingSearchTerm: state?.pendingSearchTerm,
    pendingSearchContext: state?.pendingSearchContext,
    shouldOpenSearchModal: state?.shouldOpenSearchModal,
    shouldScrollToBranchesListOnNavigate: state?.shouldScrollToBranchesListOnNavigate,
  }));

  const navigateToCityPage = useCallback(async (place: Place): Promise<void> => {
    const city = await getCitySlug(place.city, place.state);

    if (!city?.slug) {
      return;
    }

    const baseUrl = `/cidades/${city.slug}`;
    const link = buildUrlWithPersistedParams({
      baseUrl,
      overrides: {
        [PersistedUserNavigationSearchParams.CIDADE]: city.slug,
      },
    });

    const finalLink = shouldScrollToBranchesListOnNavigate
      ? `${link}#${branchesListId}`
      : link;

    await router.push(finalLink);
  }, [router, shouldScrollToBranchesListOnNavigate]);

  const persistCityParam = useCallback((place: Place) => {
    searchParams.setParams({
      [PersistedUserNavigationSearchParams.CIDADE]: slugify(
        `${place.city}-${place.state}`,
      ),
    });
  }, [searchParams]);

  const updateUserLocation = useCallback((place: Place) => {
    setUserLocation(place);
  }, []);

  const handlePostLocationActions = useCallback(async (place: Place) => {
    setOpenLocationSelectionModal(false);

    if (locationCallback) {
      persistCityParam(place);
      await new Promise((resolve) => setTimeout(resolve, MODAL_CLOSE_DELAY));
      locationCallback(place);
      if (pendingSearchTerm !== undefined) {
        openSearchModal(true, pendingSearchTerm, pendingSearchContext);
      }
    } else {
      await navigateToCityPage(place);
      if (pendingSearchTerm !== undefined) {
        openSearchModal(true, pendingSearchTerm, pendingSearchContext);
      } else if (shouldOpenSearchModal) {
        openSearchModal(true, undefined, pendingSearchContext);
      }
    }
  }, [
    locationCallback,
    pendingSearchTerm,
    pendingSearchContext,
    shouldOpenSearchModal,
    navigateToCityPage,
    persistCityParam,
  ]);

  const selectLocation = useCallback(async (
    place: Place,
    prediction?: AutocompletePrediction,
    analyticsEvent?: typeof AnalyticsEnum[keyof typeof AnalyticsEnum],
  ) => {
    if (analyticsEvent) {
      analytics.track(analyticsEvent, {
        cidade: prediction?.description || place.city,
      });
    }

    resetSessionToken();
    updateUserLocation(place);

    if (prediction) {
      addRecentSearch(prediction);
    }

    await handlePostLocationActions(place);
  }, [analytics, resetSessionToken, updateUserLocation, addRecentSearch, handlePostLocationActions]);

  const cancelGeolocation = useCallback((): void => {
    if (!geolocationRequestRef.current) {
      return;
    }

    geolocationRequestRef.current = null;
    stopGeolocating();
  }, [stopGeolocating]);

  const handleMyLocation = useCallback(async (): Promise<void> => {
    analytics.track(AnalyticsEnum.USE_MY_LOCATION);

    const request = Symbol('geolocation');
    geolocationRequestRef.current = request;

    const isCurrentRequest = (): boolean => geolocationRequestRef.current === request;

    setHasGeolocationError(false);
    startGeolocating();

    try {
      const location = await navigator.permissions.query({
        name: 'geolocation',
      });

      if (location.state === 'denied') {
        setDeniedGeolocation(true);
        return;
      }

      const place = await getGoogleMapsPlaceByCoordinates();

      if (!isCurrentRequest()) {
        return;
      }

      await selectLocation(place);
    } catch (error) {
      if (!isCurrentRequest()) {
        return;
      }

      logger.error(
        'Failed to resolve the user geolocation',
        {
          event: 'location.selection.geolocation_failed',
          location: { stage: 'geolocation', target: 'geocoder' },
          error,
        },
      );

      if (isGeolocationPermissionDenied(error)) {
        setDeniedGeolocation(true);
        return;
      }

      setHasGeolocationError(true);
    } finally {
      if (isCurrentRequest()) {
        geolocationRequestRef.current = null;
        stopGeolocating();
      }
    }
  }, [analytics, getGoogleMapsPlaceByCoordinates, selectLocation, startGeolocating, stopGeolocating]);

  const handleSearchResultClick = useCallback(async (
    prediction: AutocompletePrediction,
  ) => {
    setHasSelectionError(false);

    try {
      const predictions = prediction.placeId
        ? []
        : await getGoogleMapsPredictions(prediction.description);

      const placeId = prediction.placeId || predictions[0]?.placeId;

      if (!placeId) {
        logger.warn('No place matched the selected search result', {
          event: 'location.selection.place_not_matched',
          location: { stage: 'search_result', target: 'places' },
        });
        setHasSelectionError(true);
        return;
      }

      const place = await getGoogleMapsPlaceDetailsWithGeocoder(placeId);

      await selectLocation(
        place,
        prediction,
        AnalyticsEnum.CLICK_SEARCH_RESULT_CITY,
      );
    } catch (error) {
      logger.error(
        'Failed to select a search result',
        {
          event: 'location.selection.search_result_failed',
          location: { stage: 'search_result', target: 'geocoder' },
          error,
        },
      );
      setHasSelectionError(true);
    }
  }, [getGoogleMapsPredictions, getGoogleMapsPlaceDetailsWithGeocoder, selectLocation]);

  const handleRegisterAddressClick = useCallback(async (address: CustomerAddress) => {
    setHasSelectionError(false);

    try {
      const place = await getGoogleMapsPlaceByAddress(addressFormatter(address, 'forSearchMaps'))
        ?? await getGoogleMapsPlaceByCityAndState(address.city, address.state);

      if (!place) {
        logger.warn('No place matched the register address', {
          event: 'location.selection.place_not_matched',
          location: { stage: 'register_address', target: 'geocoder' },
        });
        setHasSelectionError(true);
        return;
      }

      await selectLocation(
        place,
        buildPredictionFromPlace(place),
        AnalyticsEnum.CLICK_SEARCH_RESULT_CITY,
      );
    } catch (error) {
      logger.error(
        'Failed to select the register address',
        {
          event: 'location.selection.register_address_failed',
          location: { stage: 'register_address', target: 'geocoder' },
          error,
        },
      );
      setHasSelectionError(true);
    }
  }, [getGoogleMapsPlaceByAddress, getGoogleMapsPlaceByCityAndState, selectLocation]);

  const handleSuggestionClick = useCallback(async (place: Place) => {
    setHasSelectionError(false);

    try {
      await selectLocation(
        place,
        buildPredictionFromPlace(place),
        AnalyticsEnum.CLICK_MOST_SEARCHED_CITY,
      );
    } catch (error) {
      logger.error(
        'Failed to select a suggested city',
        {
          event: 'location.selection.suggestion_failed',
          location: { stage: 'suggestion' },
          error,
        },
      );
      setHasSelectionError(true);
    }
  }, [selectLocation]);

  const clearSelectionError = useCallback(() => {
    setHasSelectionError(false);
    setHasGeolocationError(false);
  }, []);

  return {
    hasSelectionError,
    hasGeolocationError,
    isGeolocating,
    clearSelectionError,
    cancelGeolocation,
    handleMyLocation,
    handleSearchResultClick,
    handleRegisterAddressClick,
    handleSuggestionClick,
  };
}
